// @ts-check
import { gql } from '@apollo/client/core';
import { Image } from '@react-pdf/renderer';
import React from 'react';
import {
  Box,
  Flex,
  Page,
  Product,
  Registration,
  Stencil,
  Text,
  Wine,
} from '../../pdf-design-system';
import {
  PagesProvider,
  usePage,
} from '../../pdf-design-system/Base/PageLayout/Page';
import UserLocaleProvider from '../../Providers/UserLocaleProvider';
import BookmarkIcon from '../../components/BookmarkIcon';
import gothamBold from './fonts/gothamBold';

import gothamBook from './fonts/gothamBook';
// import stencil from './images/stencil1.png';
// https://res.cloudinary.com/bottlebooks/image/upload/v1652016491/pdf-objects/Meininger/stencil1.png
// import stencil2 from './images/stencil2.png';
// https://res.cloudinary.com/bottlebooks/image/upload/v1652016491/pdf-objects/Meininger/stencil2.png
import messages from './locale/messages';

// These are used to generate the QR codes
// Not sure how we can handle this better.
// Do we need to add a resolver for this?
const SITE_MAPPING = {};

// This is a brand-led event. Each producer gets their own page.
const premiumRegistrations = [
  { name: 'Champagne Characters', companyId: '54e30d9c8455a697b948bc5b' },
  { name: 'Champagne Gosset', companyId: '55f7d273cbf8c2aeb644fb59' },
  { name: 'Champagne Laurent-Perrier', companyId: '624ea2f0eaa5f910a144bf64' },
  {
    name: 'Kierdorf Wein GmbH & Co. KG',
    companyId: '60a78e43d570171a0549de73',
  },
  { name: 'Vinaturel GmbH', companyId: '5e1f65adf5daf879e5402c33' },
  { name: 'VRANKEN POMMERY', companyId: '57a1f191d5d44bac0b72cff8' },
  { name: 'Wein Wolf GmbH', companyId: '5eb4dbc7a25ed00520f6bce1' },

  // Other importers that should have brand displayed instead of importer profile
  { name: '75cl', companyId: '622710c1d5028564440a647a' },
  { name: 'Alpina', companyId: '624a9e35b2ac7a0f0d543830' },
  { name: 'Eggerssohn', companyId: '57c923b2b7edb7e05561e2d0' },
  { name: 'Grand Cru Select', companyId: '5e1f658af5daf879e5402b51' },
  { name: 'Peter Riegel', companyId: '59340e8ed2af3207697b95d4' },
  { name: 'Weinwolf', companyId: '5eb4dbc7a25ed00520f6bce1' },
  { name: 'Veritable', companyId: '5d7b861b189f036f24805ee6' },
  { name: 'Weinkontor Freund', companyId: '5e3a8414698ad90a4171805c' },
  { name: 'Lobenberg', companyId: '623dbb82e100a30f11fcb267' },
  { name: 'Schlumberger', companyId: '57c40831b7edb7e05561df22' },
  { name: 'Fischer', companyId: '6295f469d8602f107921753e' },
];

export default function Finest100({ data, locale, ...rest }) {
  const { collectionId } = data;
  const unformattedSections = afterQuery(data, rest);
  const sections = unformattedSections.reduce((acc, section) => {
    const companyId = section.registration.companyId;
    const premiumRegistration = premiumRegistrations.find(
      (premiumReg) => premiumReg.companyId === companyId
    );
    if (!premiumRegistration) {
      acc.push(section);
      return acc;
    }

    // Convert the producer to a full section profile
    const producers = section.registration.registeredProducts.byProducer;
    producers.map((producer) => {
      const reformattedSection = {
        ...section,
        registration: {
          ...section.registration,
          profile: producer?.nodes[0]?.producer?.profile,
          registeredProducts: producer,
        },
      };
      acc.push(reformattedSection);
    });
    return acc;
  }, []);
  return (
    <PagesProvider>
      <UserLocaleProvider locale={locale} messages={messages}>
        {sections.map((_, index) => (
          <React.Fragment>
            <CatalogPage1
              key={`header_${index}`}
              tables={sections.slice(index, index + 1)}
              theme={theme}
              baseUrl={SITE_MAPPING[collectionId]}
              {...rest}
            />

            <CatalogPage2
              tables={sections.slice(index, index + 1)}
              theme={theme}
              baseUrl={SITE_MAPPING[collectionId]}
              {...rest}
            />
          </React.Fragment>
        ))}
      </UserLocaleProvider>
    </PagesProvider>
  );
}

const standardPadding = '0.4in';

function CatalogPage1({ tables, theme, bleed = 3, style, baseUrl, ...rest }) {
  const { alignment } = usePage();
  // Only 1 table
  const { registration } = tables[0];
  const profile = registration?.profile || {};

  const [email, domain] = registration?.profile?.email.split('@');

  return (
    <Page
      size={{ width: `${180 + 2 * bleed}mm`, height: `${148 + 2 * bleed}mm` }}
      style={[
        { paddingVertical: `${bleed}mm`, paddingHorizontal: `${bleed}mm` },
        style,
      ]}
      theme={theme}
      {...rest}
    >
      {/* <Stencil
        src={stencil}
        style={{ height: '147mm', padding: `${bleed}mm` }}
      /> */}
      <Flex
        style={{
          position: 'absolute',
          backgroundColor: theme.colors.primary,
          top: 0,
          left: 0,
          right: 0,
          height: `${10 + bleed}mm`,
          paddingHorizontal: `${10 + bleed}mm`,
          paddingRight: `${17 + bleed}mm`,
          paddingTop: `${bleed}mm`,
          alignItems: 'center',
        }}
      >
        <Text style={{ color: 'white' }}>
          {registration.stand?.name || 'Tisch 999'}
        </Text>
      </Flex>
      <Box
        style={{
          paddingHorizontal: '10mm',
          paddingRight: '17mm',
          paddingTop: '14mm',
          paddingBottom: '10mm',
          height: '148mm',
          position: 'relative',
        }}
      >
        <Box key={registration.registrationId} style={{ height: '123mm' }}>
          <Registration.Provider value={registration}>
            <Flex
              style={{ borderBottom: 1, borderColor: theme.colors.primary }}
            >
              <Box style={{ height: '35mm', width: '55mm' }}>
                {profile?.autoSizedLogo?.fixed?.src && (
                  <Image
                    src={profile.autoSizedLogo.fixed.src}
                    style={{
                      maxHeight: '43mm',
                      maxWidth: '55mm',
                      marginHorizontal: 'auto',
                    }}
                  />
                )}
              </Box>
              <Box
                style={{
                  width: '93mm',
                  flexShrink: 0,
                  flexGrow: 0,
                  marginLeft: '5mm',
                  height: '40mm',
                }}
              >
                <Text>{profile.shortDescription}</Text>
              </Box>
            </Flex>
            <Flex style={{ marginTop: '7.5mm' }}>
              <Flex direction="column" style={{ width: '55mm' }}>
                <Box style={{ height: '41mm' }}>
                  <Text variant="boldAccent">{profile.name}</Text>
                  <Text>{profile.street}</Text>
                  <Text>{[profile.postalCode, profile.city].join(' ')}</Text>
                  <Text>{profile.country}</Text>
                  <Text>{profile.telephone}</Text>
                  <Text>
                    {profile.email !== 'newbrand@bottlebooks.me'
                      ? `${email}@`
                      : ' '}
                  </Text>
                  <Text>{domain}</Text>
                </Box>
                <Box
                  style={{
                    borderTop: 1,
                    borderColor: theme.colors.primary,
                    paddingTop: '3mm',
                  }}
                >
                  <Text variant="boldAccent">Importeur / Händler</Text>
                  <DistributionContacts registration={registration} />
                </Box>
              </Flex>
              <Box style={{ height: '75mm', marginLeft: '5mm' }}>
                {profile?.autoSizedMainImage?.fixed?.src && (
                  <Image
                    src={profile.autoSizedMainImage.fixed.src}
                    style={{
                      maxHeight: '75mm',
                      maxWidth: '93mm',
                      marginHorizontal: 'auto',
                    }}
                  />
                )}
              </Box>
            </Flex>
          </Registration.Provider>
        </Box>
      </Box>
    </Page>
  );
}

function DistributionContacts({ registration }) {
  const isSeekingDistribution = registration.registeredProducts?.nodes?.find(
    (product) => product.producer?.isSeekingDistribution
  );
  if (isSeekingDistribution) return <Text>Distribution gesucht</Text>;
  const distributionContactsMap =
    registration.registeredProducts?.nodes?.reduce((acc, product) => {
      product.producer.distributionContacts?.map(
        (contact) => (acc[contact.contactId] = contact)
      );
      return acc;
    }, {});
  const distributionContacts = Object.values(distributionContactsMap);

  return (
    <Box>
      {distributionContacts?.map(({ contactId, companyName }) => (
        <Text key={contactId}>{companyName}</Text>
      ))}
    </Box>
  );
}

function CatalogPage2({ tables, theme, bleed = 3, style, baseUrl, ...rest }) {
  const { alignment } = usePage();
  // Only 1 table
  const { registration } = tables[0];
  return (
    <Page
      size={{ width: `${180 + 2 * bleed}mm`, height: `${148 + 2 * bleed}mm` }}
      style={[
        { paddingVertical: `${bleed}mm`, paddingHorizontal: `${bleed}mm` },
        style,
      ]}
      theme={theme}
      {...rest}
    >
      {/* <Stencil
        src={stencil2}
        style={{ height: '147mm', padding: `${bleed}mm` }}
      /> */}
      <Flex
        style={{
          position: 'absolute',
          backgroundColor: theme.colors.primary,
          top: 0,
          left: 0,
          right: 0,
          height: `${10 + bleed}mm`,
          paddingHorizontal: `${10 + bleed}mm`,
          paddingLeft: `${17 + bleed}mm`,
          paddingTop: `${bleed}mm`,
          alignItems: 'center',
        }}
      >
        <Box style={{ flexGrow: 1 }}></Box>
        <Text style={{ color: 'white' }}>
          {registration.stand?.name || 'Tisch 999'}
        </Text>
      </Flex>
      <Box
        style={{
          paddingHorizontal: '10mm',
          paddingLeft: '17mm',
          paddingTop: '14mm',
          paddingBottom: '10mm',
          height: '148mm',
          position: 'relative',
        }}
      >
        <Box key={registration.registrationId} style={{ height: '123mm' }}>
          <Registration.Provider value={registration}>
            <Flex direction="column">
              {registration.registeredProducts?.nodes?.map(
                (registeredProduct, index) => {
                  const { product } = registeredProduct;
                  const analyticsString = [
                    product.designation,
                    product.alcohol && `Alk. ${product.alcohol}%`,
                    product.residualSugar && `RS ${product.residualSugar} g/l`,
                    product.totalAcidity && `GS ${product.totalAcidity} g/l`,
                  ]
                    .filter(Boolean)
                    .join(' | ');
                  return (
                    <Flex
                      key={index}
                      style={{
                        alignItems: 'center',
                        borderBottom: 1,
                        borderColor: theme.colors.primary,
                        height: '19.5mm',
                        flexShrink: 0,
                      }}
                    >
                      <Product.BottleImage
                        height={45}
                        width={20}
                        product={product}
                      />
                      <Box style={{ marginLeft: '3mm' }}>
                        <Text variant="bold">{product.name}</Text>
                        <Text>{product.denomination}</Text>
                        <Wine.Blend.Value product={product} />
                        <Text>{analyticsString}</Text>
                      </Box>
                      {registeredProduct.bookmark && (
                        <Box
                          style={{
                            border: 1,
                            borderColor: theme.colors.primary,
                            borderRadius: 100,
                            marginBottom: 6,
                            backgroundColor: '#efefef',
                            marginLeft: -30,
                            position: 'absolute',
                            top: 5,
                          }}
                        >
                          <BookmarkIcon
                            variant="filled"
                            style={{ color: theme.colors.primary }}
                          />
                        </Box>
                      )}
                    </Flex>
                  );
                }
              )}
            </Flex>
          </Registration.Provider>
        </Box>
      </Box>
    </Page>
  );
}
/**
 * @param {ReturnType<afterQuery>[number]} section
 */
function getTable(section) {
  if (section.__typename === 'RegistrationCatalogSection') {
    return section.registration;
  }
  return undefined;
}

/** @typedef {ReturnType<getTable>} Table */

/**
 * @typedef {import('../../pdf-design-system/useTheme').BottlebooksPDFTheme["theme"] & {
 *  colors?: Record<string, string>;
 * }} Theme
 */

function colors(themeId) {
  const colors = {
    finest100: {
      primary: '#69694b',
      accent: '#c41b79',
    },
    champagne: { primary: '#d9c896', accent: 'black' },
  };
  return colors[themeId];
}

/** @type {Theme} */
export const theme = {
  colors: colors('champagne'),
  components: {
    Text: {
      base: { ...gothamBook },
      default: { fontSize: 10 },
      smallest: { fontSize: 6, lineHeight: 1 },
      small: { fontSize: 8, lineHeight: 1.2 },
      boldAccent: {
        ...gothamBold,
        color: colors('champagne').accent,
        fontSize: 10,
      },
      bold: { ...gothamBold, fontSize: 10 },
    },
    Title: {
      base: { /*  ...dinMedium , */ textTransform: 'uppercase' },
      small: {
        fontSize: 8,
        lineHeight: 1,
      },
      default: {
        fontSize: 10,
        lineHeight: 1,
      },
      large: {
        fontSize: 18,
        lineHeight: 1.3,
      },
    },
  },
};

Finest100.fragment = gql`
  fragment Finest100 on CatalogConfiguration {
    catalogConfigurationId
    collectionId
    sections(filter: { registration: { companyId: { in: $companyIds } } }) {
      __typename
      ... on RegistrationCatalogSection {
        registration {
          registrationId
          companyId
        }
      }
      ... on UploadedFileCatalogSection {
        filename
      }
      ...Finest100_Table
    }
  }

  fragment Finest100_Table on RegistrationCatalogSection {
    registration {
      registrationId
      name
      companyId
      stand {
        name
      }
      profile {
        ...Finest_Profile
      }

      registeredProducts {
        nodes {
          ...Finest_RegisteredProduct
        }
        byProducer: group(field: producer___name) {
          fieldValue
          nodes {
            # We need to get the profile for the producer for premium registrations
            producer {
              profile {
                ...Finest_Profile
              }
            }
            ...Finest_RegisteredProduct
          }
        }
      }
    }
  }

  fragment Finest_Profile on Profile {
    name
    street
    postalCode
    city
    country
    telephone
    email
    website
    shortDescription
    autoSizedLogo: logo {
      # 55mm in 300dpi
      fixed(width: 654, crop: PAD, gravity: NORTH, fetch_format: JPG) {
        src
      }
    }
    autoSizedMainImage: mainImage {
      # 55mm in 300dpi
      fixed(
        width: 1105
        height: 891
        crop: FILL
        gravity: FACE_AUTO
        fetch_format: JPG
      ) {
        src
      }
    }
  }

  fragment Finest_RegisteredProduct on RegisteredProduct {
    newToMarket: customFieldValue(
      key: "isThisWineNewNewDiscoveryOrNewToMarket"
    ) {
      __typename
      ... on YesNoFieldValue {
        value
      }
    }
    producer {
      isSeekingDistribution
      distributionContacts {
        contactId
        companyName
      }
    }
    productId
    product {
      productId
      name
      alcohol

      regionHierarchy
      denomination
      ...Blend
      ...BottleImage
      ... on Wine {
        designation
        residualSugar
        totalAcidity
      }
    }
  }
  ${Product.Blend.fragment}
  ${Product.BottleImage.fragment}
`;

function afterQuery(data, rest) {
  if (rest.companyId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.companyId === rest.companyId
    );
  }
  if (rest.registrationId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.registrationId === rest.registrationId
    );
  }

  return data.sections;
}
